import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, Send } from "lucide-react";
import { BUSINESS, BRANCHES, waLink, telLink, SERVICES } from "@/lib/site-data";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        branch: "claremont",
        service: "Thai Oil Massage",
        date: "",
        notes: "",
    });

    const branch = BRANCHES.find((b) => b.id === form.branch) || BRANCHES[0];

    const onSubmit = (e) => {
        e.preventDefault();
        const text = [
            `Hi, I'd like to book at Aekkarag (${branch.name}).`,
            ``,
            `Name: ${form.name || "—"}`,
            `Phone: ${form.phone || "—"}`,
            `Service: ${form.service}`,
            form.date ? `Preferred date/time: ${form.date}` : null,
            form.notes ? `Notes: ${form.notes}` : null,
        ]
            .filter(Boolean)
            .join("\n");
        window.open(waLink(branch.whatsapp, text), "_blank", "noopener,noreferrer");
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 lg:py-36 bg-beige/60 grain"
        >
            <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5"
                >
                    <span className="eyebrow flex items-center gap-3">
                        <span className="w-8 h-px bg-gold" />
                        Get in Touch
                    </span>
                    <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-maroon tracking-tight leading-[1.05]">
                        Book your{" "}
                        <em className="font-light text-gold-dark">moment of calm</em>.
                    </h2>
                    <p className="mt-6 text-ink-soft font-light leading-relaxed">
                        Send us a quick message and we'll respond on WhatsApp with availability.
                        Walk-ins are always welcome too.
                    </p>

                    <div className="mt-10 space-y-5">
                        {BRANCHES.map((b) => (
                            <div
                                key={b.id}
                                className="border-t border-gold/20 pt-5"
                                data-testid={`contact-branch-${b.id}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h4 className="font-serif text-2xl text-maroon">
                                            {b.name}
                                        </h4>
                                        <p className="text-xs tracking-[0.2em] uppercase text-ink-muted mt-1">
                                            {b.hours}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <a
                                            href={telLink(b.phoneRaw)}
                                            className="w-10 h-10 flex items-center justify-center border border-gold/40 text-maroon hover:bg-maroon hover:text-cream transition rounded-full"
                                            aria-label={`Call ${b.name}`}
                                        >
                                            <Phone size={14} />
                                        </a>
                                        <a
                                            href={waLink(b.whatsapp)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 flex items-center justify-center bg-[#25D366] text-white hover:bg-[#1ebf5a] transition rounded-full"
                                            aria-label={`WhatsApp ${b.name}`}
                                        >
                                            <MessageCircle size={14} />
                                        </a>
                                    </div>
                                </div>
                                <p className="text-sm text-ink-soft mt-2">
                                    {b.phone} · {b.address}
                                </p>
                            </div>
                        ))}

                        <div className="border-t border-gold/20 pt-5 flex items-center gap-3">
                            <Mail size={16} className="text-gold-dark" />
                            <a
                                href={`mailto:${BUSINESS.email}`}
                                className="text-ink-soft hover:text-maroon transition text-sm"
                                data-testid="contact-email"
                            >
                                {BUSINESS.email}
                            </a>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={onSubmit}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="lg:col-span-7 bg-cream border border-gold/30 p-8 lg:p-12"
                    data-testid="booking-form"
                >
                    <h3 className="font-serif text-3xl text-maroon mb-2">Quick booking</h3>
                    <p className="text-sm text-ink-muted mb-8">
                        Fill in a few details — we'll open WhatsApp with your message ready to send.
                    </p>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <Field label="Your name">
                            <input
                                required
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="e.g. Sarah"
                                data-testid="form-name"
                                className="form-input"
                            />
                        </Field>
                        <Field label="Phone">
                            <input
                                required
                                type="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="e.g. 082 555 1234"
                                data-testid="form-phone"
                                className="form-input"
                            />
                        </Field>
                        <Field label="Branch">
                            <select
                                value={form.branch}
                                onChange={update("branch")}
                                data-testid="form-branch"
                                className="form-input"
                            >
                                {BRANCHES.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name} — {b.address.split(",")[0]}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Service">
                            <select
                                value={form.service}
                                onChange={update("service")}
                                data-testid="form-service"
                                className="form-input"
                            >
                                {SERVICES.map((s) => (
                                    <option key={s.id} value={s.name}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Preferred date / time" full>
                            <input
                                type="text"
                                value={form.date}
                                onChange={update("date")}
                                placeholder="e.g. Saturday 3pm"
                                data-testid="form-date"
                                className="form-input"
                            />
                        </Field>
                        <Field label="Notes (optional)" full>
                            <textarea
                                rows={3}
                                value={form.notes}
                                onChange={update("notes")}
                                placeholder="Any preferences or focus areas?"
                                data-testid="form-notes"
                                className="form-input"
                            />
                        </Field>
                    </div>

                    <button
                        type="submit"
                        data-testid="form-submit"
                        className="btn-gold mt-8 w-full sm:w-auto justify-center"
                    >
                        <Send size={16} />
                        Send via WhatsApp
                    </button>
                </motion.form>
            </div>

            {/* Local form input styles */}
            <style>{`
                .form-input {
                    width: 100%;
                    background: #FAF7F2;
                    border: 1px solid rgba(201, 169, 97, 0.4);
                    padding: 0.75rem 1rem;
                    color: #1A1515;
                    font-family: 'Manrope', sans-serif;
                    font-size: 0.95rem;
                    border-radius: 2px;
                    transition: border-color 0.25s ease;
                    outline: none;
                }
                .form-input:focus {
                    border-color: #C9A961;
                    box-shadow: 0 0 0 3px rgba(201, 169, 97, 0.15);
                }
                .form-input::placeholder { color: #b3a8a8; }
            `}</style>
        </section>
    );
}

function Field({ label, full, children }) {
    return (
        <label className={`flex flex-col gap-2 ${full ? "sm:col-span-2" : ""}`}>
            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-gold-dark font-semibold">
                {label}
            </span>
            {children}
        </label>
    );
}
